if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    initializeViewer() {
      if (this.viewerReady) return;
      this.viewerReady = true;
      this.dataset.btlGallery = '';
      this.mediaContent = this.querySelector('.product-media-modal__content');
      this.mediaDialog = this.querySelector('[role="dialog"]');
      this.allMedia = Array.from(this.mediaContent.children).filter(media => media.hasAttribute('data-media-id'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionToken = 0;
      const makeArrow = (direction, label) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btl-media-arrow btl-media-arrow--' + direction;
        button.setAttribute('aria-label', label);
        button.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="' + (direction === 'prev' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6') + '"/></svg>';
        button.addEventListener('click', () => this.changeMedia(direction === 'prev' ? -1 : 1));
        this.mediaDialog.appendChild(button);
        return button;
      };
      this.previousButton = makeArrow('prev', 'Previous media');
      this.nextButton = makeArrow('next', 'Next media');
      this.dots = document.createElement('div');
      this.dots.className = 'btl-media-dots';
      this.dots.setAttribute('role', 'group');
      this.dots.setAttribute('aria-label', 'Choose product media');
      this.mediaDialog.appendChild(this.dots);
      this.mediaStatus = document.createElement('span');
      this.mediaStatus.className = 'visually-hidden';
      this.mediaStatus.setAttribute('aria-live', 'polite');
      this.mediaStatus.setAttribute('aria-atomic', 'true');
      this.mediaDialog.appendChild(this.mediaStatus);
      this.mediaDialog.addEventListener('pointerup', event => event.stopPropagation());
      this.addEventListener('keydown', event => {
        if (!this.hasAttribute('open') || event.target.closest('input, textarea, select, video, iframe, model-viewer, deferred-media, product-model')) return;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          event.stopPropagation();
          const fromDots = this.dots.contains(event.target);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.mediaItems.length - 1 : this.mediaIndex + (event.key === 'ArrowRight' ? 1 : -1);
          this.setActiveMedia(Math.max(0, Math.min(next, this.mediaItems.length - 1)));
          if (fromDots) this.dots.children[this.mediaIndex]?.focus({ preventScroll: true });
        }
      });
      this.initializeZoom();
      window.addEventListener('resize', () => {
        if (this.hasAttribute('open')) {
          this.resetMotion();
          this.resetZoom();
        }
      });
    }

    initializeZoom() {
      this.zoom = 1;
      this.panX = this.panY = 0;
      const zoomHelp = document.createElement('span');
      zoomHelp.id = this.id + '-zoom-help';
      zoomHelp.className = 'visually-hidden';
      zoomHelp.textContent = 'Click with a mouse or double-tap an image to zoom. Drag to pan when enlarged. Use plus or minus to zoom, and zero to fit.';
      this.mediaDialog.appendChild(zoomHelp);
      this.mediaDialog.setAttribute('aria-describedby', [this.mediaDialog.getAttribute('aria-describedby'), zoomHelp.id].filter(Boolean).join(' '));
      this.mediaContent.addEventListener('dragstart', event => {
        if (event.target.tagName === 'IMG') event.preventDefault();
      });
      this.mediaContent.addEventListener('load', event => {
        if (event.target === this.activeImage() && this.hasAttribute('open')) this.resetZoom();
      }, true);

      this.mediaContent.addEventListener('pointerdown', event => this.startGesture(event));
      this.mediaContent.addEventListener('pointermove', event => this.moveGesture(event));
      this.mediaContent.addEventListener('pointerup', event => this.endGesture(event, false));
      this.mediaContent.addEventListener('pointercancel', event => this.endGesture(event, true));
      this.mediaContent.addEventListener('lostpointercapture', event => {
        if (this.gesture?.id === event.pointerId) this.endGesture(event, true);
      });
      this.addEventListener('keydown', event => {
        if (!this.hasAttribute('open') || !this.activeImage() || event.ctrlKey || event.metaKey || event.altKey || event.target.isContentEditable || event.target.closest('input, textarea, select')) return;
        if (['+', '=', '-', '0'].includes(event.key)) {
          event.preventDefault();
          this.setZoom(event.key === '0' ? 1 : this.zoom + (event.key === '-' ? -.5 : .5));
        }
      });
    }

    activeImage() {
      const media = this.mediaItems?.[this.mediaIndex];
      return media?.tagName === 'IMG' ? media : null;
    }

    imageBounds() {
      const image = this.activeImage();
      if (!image?.complete || !image.naturalWidth || !image.clientWidth || !image.clientHeight) return null;
      const width = image.clientWidth;
      const height = image.clientHeight;
      const fit = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      return { image, width, height, fitWidth: image.naturalWidth * fit, fitHeight: image.naturalHeight * fit };
    }

    clampPan(bounds) {
      const maxX = Math.max(0, (bounds.fitWidth * this.zoom - bounds.width) / 2);
      const maxY = Math.max(0, (bounds.fitHeight * this.zoom - bounds.height) / 2);
      this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
      this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
    }

    paintZoom() {
      const image = this.activeImage();
      if (!image) return;
      image.style.translate = this.panX + 'px ' + this.panY + 'px';
      image.style.scale = String(this.zoom);
      image.classList.toggle('btl-media-zoomed', this.zoom > 1);
    }

    stopZoomAnimation(adopt = false) {
      const animation = this.zoomAnimation;
      if (!animation) return;
      this.zoomAnimation = null;
      const image = this.activeImage();
      if (adopt && image) {
        const style = getComputedStyle(image);
        this.zoom = parseFloat(style.scale) || 1;
        const [x, y] = style.translate.split(' ');
        this.panX = parseFloat(x) || 0;
        this.panY = parseFloat(y) || 0;
      }
      animation.cancel();
      if (adopt) this.paintZoom();
    }

    setZoom(value, point = null) {
      const bounds = this.imageBounds();
      if (!bounds || this.isSliding) return;
      this.stopZoomAnimation(true);
      const from = { scale: String(this.zoom), translate: this.panX + 'px ' + this.panY + 'px' };
      const next = Math.max(1, Math.min(3, value));
      const ratio = next / this.zoom;
      const rect = point ? bounds.image.getBoundingClientRect() : null;
      const x = point ? point.clientX - (rect.left + rect.width / 2 - this.panX) : 0;
      const y = point ? point.clientY - (rect.top + rect.height / 2 - this.panY) : 0;
      this.panX = this.panX * ratio + x * (1 - ratio);
      this.panY = this.panY * ratio + y * (1 - ratio);
      this.zoom = next;
      this.clampPan(bounds);
      this.paintZoom();
      if (!this.reducedMotion.matches && bounds.image.animate) {
        const animation = bounds.image.animate([from, {
          scale: String(this.zoom), translate: this.panX + 'px ' + this.panY + 'px'
        }], { duration: 220, easing: 'cubic-bezier(.22,.61,.36,1)' });
        this.zoomAnimation = animation;
        bounds.image.classList.add('btl-media-zoomed');
        animation.onfinish = () => {
          if (this.zoomAnimation !== animation) return;
          this.zoomAnimation = null;
          this.paintZoom();
        };
      }

      

      this.mediaStatus.textContent = 'Image zoom ' + Math.round(next * 100) + ' percent';
    }

    resetZoom() {
      this.stopZoomAnimation();
      this.cancelGesture();
      this.zoom = 1;
      this.panX = this.panY = 0;
      this.lastTap = null;
      (this.allMedia || []).forEach(media => {
        media.style.removeProperty('translate');
        media.style.removeProperty('scale');
        media.classList.remove('btl-media-zoomed', 'btl-media-panning');
      });

    }

    cancelGesture() {
      const gesture = this.gesture;
      this.gesture = null;
      if (this.gestureFrame) cancelAnimationFrame(this.gestureFrame);
      this.gestureFrame = 0;
      if (gesture?.image.hasPointerCapture(gesture.id)) gesture.image.releasePointerCapture(gesture.id);
      gesture?.image.classList.remove('btl-media-panning');
      this.swipe = null;
    }

    startGesture(event) {
      if (!event.isPrimary) {
        this.cancelGesture();
        this.lastTap = null;
        this.resetMotion();
        return;
      }
      const bounds = this.imageBounds();
      if (!bounds || event.target !== bounds.image || this.isSliding || event.button !== 0 || (window.visualViewport?.scale || 1) > 1) return;
      this.stopZoomAnimation(true);

      this.gesture = { ...bounds, id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, dy: 0,
        panX: this.panX, panY: this.panY, zoomed: this.zoom > 1, moved: false, time: performance.now(), pointerType: event.pointerType };
      if (this.zoom > 1) bounds.image.classList.add('btl-media-panning');
      else this.swipe = { dx: 0, axis: null, width: bounds.width, neighbor: null, direction: 0 };
      bounds.image.setPointerCapture(event.pointerId);
    }

    moveGesture(event) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      gesture.dx = event.clientX - gesture.x;
      gesture.dy = event.clientY - gesture.y;
      if (Math.max(Math.abs(gesture.dx), Math.abs(gesture.dy)) > 8) {
        gesture.moved = true;
        this.lastTap = null;
      }
      if (!gesture.zoomed && this.swipe && !this.swipe.axis && gesture.moved) this.swipe.axis = Math.abs(gesture.dx) > Math.abs(gesture.dy) * 1.2 ? 'x' : 'y';
      if (!gesture.zoomed && this.swipe?.axis !== 'x') return;
      if (event.cancelable) event.preventDefault();
      if (!this.gestureFrame) this.gestureFrame = requestAnimationFrame(() => {
        this.gestureFrame = 0;
        this.paintGesture();
      });
    }

    paintGesture() {
      const gesture = this.gesture;
      if (!gesture) return;
      if (gesture.zoomed) {
        this.panX = gesture.panX + gesture.dx;
        this.panY = gesture.panY + gesture.dy;
        this.clampPan(gesture);
        this.paintZoom();
        return;
      }
      const swipe = this.swipe;
      if (swipe?.axis !== 'x') return;
      const direction = gesture.dx < 0 ? 1 : -1;
      const neighbor = this.mediaItems[this.mediaIndex + direction];
      if (swipe.neighbor && swipe.neighbor !== neighbor) {
        swipe.neighbor.classList.remove('btl-media-adjacent');
        swipe.neighbor.style.transform = '';
      }
      swipe.direction = direction;
      swipe.neighbor = neighbor;
      swipe.dx = neighbor ? Math.max(-swipe.width, Math.min(swipe.width, gesture.dx)) : gesture.dx * .2;
      gesture.image.style.transform = 'translateX(' + swipe.dx + 'px)';
      if (neighbor) {
        neighbor.classList.add('btl-media-adjacent');
        neighbor.inert = true;
        neighbor.setAttribute('aria-hidden', 'true');
        neighbor.style.transform = 'translateX(' + (swipe.dx + direction * swipe.width) + 'px)';
      }
    }

    endGesture(event, cancelled) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      if (this.gestureFrame) cancelAnimationFrame(this.gestureFrame);
      this.gestureFrame = 0;
      if (!cancelled) this.paintGesture();
      this.gesture = null;
      gesture.image.classList.remove('btl-media-panning');
      if (gesture.image.hasPointerCapture(gesture.id)) gesture.image.releasePointerCapture(gesture.id);
      if (cancelled) {
        this.lastTap = null;
        this.resetMotion();
        return;
      }
      if (!gesture.zoomed) this.finishSwipe(false);
      if (!gesture.moved && gesture.pointerType === 'mouse') {
        this.setZoom(this.zoom > 1 ? 1 : 2, event);
      }
      if (!gesture.moved && gesture.pointerType === 'touch' && performance.now() - gesture.time < 350) {
        const tap = this.lastTap;
        if (tap && performance.now() - tap.time < 320 && Math.hypot(event.clientX - tap.x, event.clientY - tap.y) < 24) {
          this.lastTap = null;
          this.setZoom(this.zoom > 1 ? 1 : 2, event);
        } else this.lastTap = { x: event.clientX, y: event.clientY, time: performance.now() };
      }
    }

    resetMotion() {
      this.motionToken = (this.motionToken || 0) + 1;
      (this.animations || []).forEach(animation => animation.cancel());
      this.animations = [];
      this.isSliding = false;
      this.swipe = null;
      this.mediaContent?.classList.remove('btl-media-sliding');
      (this.allMedia || []).forEach(media => {
        media.style.transform = '';
        media.classList.remove('btl-media-adjacent');
        media.inert = !media.classList.contains('active');
        media.setAttribute('aria-hidden', String(media.inert));
      });
    }

    animateMedia(moves) {
      if (this.reducedMotion.matches || !moves.length || !moves[0].media.animate) {
        this.resetMotion();
        return;
      }
      const token = this.motionToken;
      this.isSliding = true;
      this.mediaContent.classList.add('btl-media-sliding');
      this.animations = moves.map(({ media, from, to }) => media.animate([
        { transform: 'translateX(' + from + 'px)' },
        { transform: 'translateX(' + to + 'px)' }
      ], { duration: 280, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' }));
      Promise.allSettled(this.animations.map(animation => animation.finished)).then(() => {
        if (token === this.motionToken) this.resetMotion();
      });
    }

    finishSwipe(cancelled) {
      const swipe = this.swipe;
      if (!swipe) return;
      this.swipe = null;
      if (swipe.axis !== 'x') {
        this.resetMotion();
        return;
      }
      if (!cancelled && swipe.neighbor && Math.abs(swipe.dx) >= Math.min(80, swipe.width * .2)) {
        this.setActiveMedia(this.mediaIndex + swipe.direction, false, swipe);
        return;
      }
      const moves = [{ media: this.mediaItems[this.mediaIndex], from: swipe.dx, to: 0 }];
      if (swipe.neighbor) moves.push({ media: swipe.neighbor,
        from: swipe.dx + swipe.direction * swipe.width, to: swipe.direction * swipe.width });
      this.animateMedia(moves);
    }

    hide() {
      this.resetZoom();
      this.resetMotion();
      super.hide();
    }

    show(opener) {
      this.initializeViewer();
      this.resetZoom();
      this.resetMotion();
      super.show(opener);
      this.showActiveMedia();
    }

    showActiveMedia() {
      const mediaId = this.openedBy.getAttribute('data-media-id');
      const activeMedia = this.allMedia.find(media => media.getAttribute('data-media-id') === mediaId) || this.allMedia[0];
      if (!activeMedia) return;
      this.mediaItems = this.allMedia.filter(media => !media.classList.contains('product__media-item--variant') || media === activeMedia);
      this.mediaItems.forEach(media => {
        if (media.tagName === 'IMG') media.loading = 'eager';
      });
      this.dots.replaceChildren();
      this.mediaItems.forEach((media, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'btl-media-dot';
        dot.style.left = index * 16 + 'px';
        dot.setAttribute('aria-label', 'Show media ' + (index + 1) + ' of ' + this.mediaItems.length);
        dot.addEventListener('click', () => this.setActiveMedia(index));
        this.dots.appendChild(dot);
      });
      this.dots.hidden = this.mediaItems.length < 2;
      this.dots.style.width = Math.min(6, this.mediaItems.length) * 16 + 'px';
      this.setActiveMedia(this.mediaItems.indexOf(activeMedia), true);
    }

    changeMedia(direction) {
      const index = this.mediaIndex + direction;
      if (index >= 0 && index < this.mediaItems.length) this.setActiveMedia(index);
    }

    setActiveMedia(index, immediate = false, swipe = null) {
      const activeMedia = this.mediaItems[index];
      if (!activeMedia || this.isSliding || (!immediate && index === this.mediaIndex)) return;
      const previousMedia = this.mediaItems[this.mediaIndex];
      const direction = index > this.mediaIndex ? 1 : -1;
      const width = swipe?.width || previousMedia?.getBoundingClientRect().width || 0;
      const offset = swipe?.dx || 0;
      const focusedElement = document.activeElement;
      this.resetZoom();
      this.resetMotion();
      window.pauseAllMedia();
      if (previousMedia?.contains(focusedElement)) this.mediaDialog.focus();
      this.allMedia.forEach(media => {
        media.classList.toggle('active', media === activeMedia);
        media.inert = media !== activeMedia;
        media.setAttribute('aria-hidden', String(media !== activeMedia));
      });
      this.mediaIndex = index;
      
      this.mediaContent.scrollTop = 0;
      this.mediaContent.scrollLeft = 0;
      this.previousButton.disabled = index === 0;
      this.nextButton.disabled = index === this.mediaItems.length - 1;
      const windowStart = Math.max(0, Math.min(index - 3, this.mediaItems.length - 6));
      this.dots.style.setProperty('--btl-dot-offset', -windowStart * 16 + 'px');
      Array.from(this.dots.children).forEach((dot, dotIndex) => {
        const visible = dotIndex >= windowStart && dotIndex < windowStart + 6;
        dot.setAttribute('aria-current', String(dotIndex === index));
        dot.style.setProperty('--btl-dot-scale', Math.max(.4, 1 - Math.max(0, Math.abs(dotIndex - index) - 1) * .2));
        dot.inert = !visible;
        dot.tabIndex = dotIndex === index ? 0 : -1;
      });
      this.mediaStatus.textContent = 'Media ' + (index + 1) + ' of ' + this.mediaItems.length;
      const template = activeMedia.querySelector('template');
      if (activeMedia.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) activeMedia.loadContent();
      const keepFocus = this.contains(focusedElement) && !focusedElement.disabled && !focusedElement.closest('[inert]') && focusedElement.getClientRects().length;
      trapFocus(this, keepFocus ? focusedElement : this.mediaDialog);
      if (!immediate && previousMedia && previousMedia !== activeMedia && width) {
        previousMedia.classList.add('btl-media-adjacent');
        this.animateMedia([
          { media: previousMedia, from: offset, to: -direction * width },
          { media: activeMedia, from: offset + direction * width, to: 0 }
        ]);
      }
    }
  });
}
